import { useMemo } from 'react';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  ABody,
  ABodyColor,
  ABodySize,
  AEyebrow,
  AEyebrowVariant,
  AHeader,
  AHeaderSize,
  AIcon,
  AIconSize,
  MButton,
  MButtonSize,
  MButtonType,
  MTextBlockSimpleAlignment,
} from '@marriott/mi-web-ui-kit';
import { useSession } from '@marriott/mi-headless-utils';
import { convertToCommaSeparatedNumber } from '@marriott/shared/mi-helper-utils';
import type { MerchandisingLayeredFeatureProps } from './MerchandisingLayeredFeature.types';
import { MarkDownText } from '../../molecules/markDown/MarkDown';
import { SkeletonLoaderStyles } from '../../skeleton-loader.styles';
import {
  LayeredFeatureTextBlockWrapper,
  StyledPhotoCaption,
  StyledPhotoCaptionText,
  StyledMerchandisingLayeredFeature,
} from './MerchandisingLayeredFeature.styles';
import { MathEquation, EqColumn, EqOperator } from './MerchandisingLayeredFeatureMath';
import clsx from 'clsx';
import {
  getIconClassFromToken,
  getStyleClassPerTheme,
  Icon,
  Image,
  LogoContainer,
  LogoContainerBackgroundColor,
} from '@marriott/mi-ui-library';
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager';
import { size } from '@marriott/mi-ui-library';

function useMathVariation(props: MerchandisingLayeredFeatureProps) {
  const { mathType = '', mathLabel1 = '', mathLabel2 = '', mathLabel3 = '', mathValue = '' } = props;
  const { datalayer } = useSession();

  const currencyType = (datalayer?.['prop_currency_type'] as string) ?? '';
  const currencyTotalStayAmount = parseFloat(`${datalayer?.['res_revenue_total_locale'] ?? '0'}`);
  const pointsTotalStayAmount = parseInt(`${datalayer?.['mr_prof_points_balance'] ?? '0'}`, 10);
  const currentNights = parseInt(`${datalayer?.['mr_prof_nights_booked_this_year'] ?? '0'}`, 10);

  const mt = mathType?.toLowerCase() ?? '';
  const isPointsVariation = mt === 'points';
  const isCurrencyVariation = mt === 'currency' && !!currencyType;
  const isNightsVariation = mt === 'nights';
  const isVariationsAvailable = isPointsVariation || isCurrencyVariation || isNightsVariation;

  const { value1, value2, value3 } = useMemo(() => {
    if (isPointsVariation) {
      const bonus = mathValue ? parseInt(mathValue.replace(/,/g, ''), 10) : 0;
      return {
        value1: convertToCommaSeparatedNumber(pointsTotalStayAmount),
        value2: mathValue,
        value3: convertToCommaSeparatedNumber(pointsTotalStayAmount + bonus),
      };
    }
    if (isCurrencyVariation) {
      const credit = mathValue ? parseFloat(mathValue.replace(/,/g, '')) : 0;
      return {
        value1: convertToCommaSeparatedNumber(currencyTotalStayAmount) + ' ' + currencyType,
        value2: mathValue + ' ' + currencyType,
        value3: convertToCommaSeparatedNumber(currencyTotalStayAmount + credit) + ' ' + currencyType,
      };
    }
    if (isNightsVariation) {
      const bonusNights = mathValue ? parseInt(mathValue.replace(/,/g, ''), 10) : 0;
      return {
        value1: convertToCommaSeparatedNumber(currentNights),
        value2: mathValue,
        value3: convertToCommaSeparatedNumber(currentNights + bonusNights),
      };
    }
    return { value1: '', value2: '', value3: '' };
  }, [
    isPointsVariation,
    isCurrencyVariation,
    isNightsVariation,
    mathValue,
    pointsTotalStayAmount,
    currencyTotalStayAmount,
    currencyType,
    currentNights,
  ]);

  return {
    variationDetails: { isVariationsAvailable, isPointsVariation, isCurrencyVariation, isNightsVariation },
    mathLabel1,
    mathLabel2,
    mathLabel3,
    value1,
    value2,
    value3,
  };
}

/** Header + description stack for the layered feature content column (co-located for readability). */
function LayeredFeatureTextContent({
  alignment = MTextBlockSimpleAlignment.Left,
  eyebrowText,
  eyebrowVariant = AEyebrowVariant.Secondary,
  headerMarkdown,
  headerSize = AHeaderSize.TitleM,
  description,
  bodySize,
  bodyColor = ABodyColor.Default,
}: {
  alignment?: MTextBlockSimpleAlignment;
  eyebrowText?: string;
  eyebrowVariant?: AEyebrowVariant;
  headerMarkdown: string;
  headerSize?: AHeaderSize;
  description?: string;
  bodySize?: ABodySize;
  bodyColor?: ABodyColor;
}) {
  const hasHeader = Boolean(headerMarkdown?.trim());

  return (
    <LayeredFeatureTextBlockWrapper
      className="d-flex flex-column"
      $centered={alignment === MTextBlockSimpleAlignment.Center}
    >
      {eyebrowText ? <AEyebrow variant={eyebrowVariant}>{eyebrowText}</AEyebrow> : null}
      {hasHeader ? (
        <MarkDownText
          className="t-title-s"
          fallback={<AHeader size={headerSize} text={headerMarkdown} />}
          fontSize={size.small}
          textContent={headerMarkdown}
          variation="title"
        />
      ) : null}
      {description ? <ABody className="p-0" bodyText={description} size={bodySize} color={bodyColor} /> : null}
    </LayeredFeatureTextBlockWrapper>
  );
}

function convertButtonSizeToMButtonSize(size: string): MButtonSize {
  const lowerCaseSize = size?.toLowerCase();

  switch (lowerCaseSize) {
    case MButtonSize.S.toLowerCase():
    case 'small':
      return MButtonSize.S;
    case MButtonSize.L.toLowerCase():
    case 'large':
      return MButtonSize.L;
    default:
      return MButtonSize.M;
  }
}

export function MerchandisingLayeredFeatureComponent(props: MerchandisingLayeredFeatureProps) {
  const {
    componentId,
    logo,
    eyebrowText,
    headerLong,
    headerMedium,
    headerShort,
    descriptionLong,
    descriptionMedium,
    descriptionShort,
    callToActionText1,
    destinationUrl1,
    openInNewTab1,
    callToActionSize1,
    callToActionSize2,
    callToActionText2,
    destinationUrl2,
    openInNewTab2,
    tertiaryLinkText,
    tertiaryLinkUrl,
    tertiaryOpenInANewTab,
    backgroundImage,
    className = '',
    trackingProperties,
    legalLong,
    theme = 'standard',
    caption,
    mboxParameter,
    isTargetContentLoaded = mboxParameter && !AuthoringUtils.isInEditor() ? false : true,
    type = 'cf',
    metadata,
  } = props;
  const header = headerLong ?? headerMedium ?? headerShort ?? '';
  const description = descriptionLong ?? descriptionMedium ?? descriptionShort ?? '';
  const hasPrimaryButton = Boolean(callToActionText1 && destinationUrl1);
  const hasSecondaryButton = Boolean(callToActionText2 && destinationUrl2);
  const hasTertiaryLink = Boolean(tertiaryLinkText && tertiaryLinkUrl);
  const math = useMathVariation(props);
  const showMathDetails = math?.variationDetails?.isVariationsAvailable;
  const themeClass = getStyleClassPerTheme(theme);
  const isInverse = themeClass === 'inverse';
  const isBrandIcon = /^(?:mcom-[^:]+:(?:brands)\/|marriott-icons:.+)/i;
  const brand = type === 'cf' ? logo?.imageSrc?.split('/').pop() : '';
  const { imageSrc, alternateDescription, renditions } = backgroundImage ?? {};
  const iconClass = getIconClassFromToken(logo?.imageSrc ?? '');
  const isCoBrand = metadata?.type === 'CoBrand';

  const layeredFeature = (
    <StyledMerchandisingLayeredFeature
      className={clsx(
        className,
        { 'cobrand-banner': showMathDetails },
        !isTargetContentLoaded && 'skeleton-loader-composite'
      )}
      $theme={theme}
      $type={type}
      data-testid={componentId || 'merch-MerchandisingLayeredFeature'}
      data-component-name={componentId || 'o-merch-MerchandisingLayeredFeature'}
    >
      <Image
        defaultImageURL={imageSrc}
        customClass="lyrd_ft__feature-img-wrapper"
        renditions={renditions}
        altText={alternateDescription}
      />
      {caption ? (
        <StyledPhotoCaption className="p-1 pr-2">
          <AIcon icon="icon-location d-flex align-items-center justify-content-center " size={AIconSize.S} />
          <StyledPhotoCaptionText className="t-font-xs">{caption}</StyledPhotoCaptionText>
        </StyledPhotoCaption>
      ) : null}
      <div
        className={clsx('lyrd_ft__cnt-sec lyrd_ft__gradient row d-flex', {
          lyrd_ft__dir: header || description,
          't-color-inverse': isInverse,
        })}
      >
        <div
          className={clsx(
            'lyrd_ft__cnt-wrapper col-12 col-xl-5 col-md-6 offset-lg-1 px-4 pr-md-2 pl-xl-2 pb-4 pb-md-0',
            showMathDetails && 'pb-2'
          )}
        >
          {type === 'cf' ? (
            logo?.imageSrc?.match(isBrandIcon) ? (
              <LogoContainer
                ariaRole="note"
                brand={brand || ''}
                aspectRatio="3:2"
                width={135}
                height={90}
                color="background-aware"
                backgroundColor={
                  isInverse ? LogoContainerBackgroundColor.Inverse : LogoContainerBackgroundColor.Standard
                }
                className="logo-icon"
              />
            ) : (
              <Icon iconClass={clsx(iconClass, 'icon icon-xs logo-icon')} data-testid="logo-icon" />
            )
          ) : (
            logo?.imageSrc && (
              <img
                src={logo?.imageSrc}
                alt={logo?.alternateDescription}
                className={clsx(isCoBrand ? 'credit-card-image' : 'logo-image')}
                data-testid="logo-image"
              />
            )
          )}
          <div className="lyrd_ft__txt-cnt">
            <LayeredFeatureTextContent
              alignment={MTextBlockSimpleAlignment.Left}
              headerMarkdown={header}
              headerSize={AHeaderSize.TitleS}
              description={description}
              bodySize={ABodySize.XS}
              bodyColor={ABodyColor.Default}
              eyebrowText={eyebrowText}
            />
          </div>

          {showMathDetails && (
            <MathEquation>
              <EqColumn label={math.mathLabel1} value={math.value1} isInverse={isInverse} />
              <EqOperator operator="+" isInverse={isInverse} />
              <EqColumn label={math.mathLabel2} value={math.value2} isInverse={isInverse} />
              <EqOperator operator="=" isInverse={isInverse} />
              <EqColumn label={math.mathLabel3} value={math.value3} isTotal isInverse={isInverse} />
            </MathEquation>
          )}
          <div className="lyrd_ft__cta-cnt d-flex align-items-center">
            {hasPrimaryButton && (
              <MButton
                variant={MButtonType.Primary}
                size={callToActionSize1 ? convertButtonSizeToMButtonSize(callToActionSize1) : MButtonSize.S}
                href={destinationUrl1}
                className={clsx(`m-button-primary`, openInNewTab1 === true && 'm-button-external')}
                trackingProperties={trackingProperties}
                custom_click_track_value={props?.custom_click_track_value_primary}
                aria-label={callToActionText1}
                target={openInNewTab1 ? '_blank' : '_self'}
              >
                {callToActionText1}
              </MButton>
            )}
            {hasSecondaryButton && (
              <MButton
                variant={MButtonType.Secondary}
                size={callToActionSize2 ? convertButtonSizeToMButtonSize(callToActionSize2) : MButtonSize.S}
                href={destinationUrl2}
                className={clsx(`m-button-secondary`, openInNewTab2 === true && 'm-button-external')}
                trackingProperties={trackingProperties}
                custom_click_track_value={`${header}|${callToActionText2}|${
                  openInNewTab2 === true ? 'external' : 'internal'
                }`}
                aria-label={callToActionText2}
                target={openInNewTab2 ? '_blank' : '_self'}
              >
                {callToActionText2}
              </MButton>
            )}
            {hasTertiaryLink && (
              <MButton
                variant={MButtonType.Tertiary}
                size={MButtonSize.S}
                href={tertiaryLinkUrl}
                openInANewTab={tertiaryOpenInANewTab}
                target={tertiaryOpenInANewTab ? '_blank' : '_self'}
                trackingProperties={trackingProperties}
                custom_click_track_value={`${header}|${tertiaryLinkText}|${
                  tertiaryOpenInANewTab === true ? 'external' : 'internal'
                }`}
                aria-label={tertiaryLinkText}
                className="tertiary-merch-button"
              >
                {tertiaryLinkText}
              </MButton>
            )}
          </div>
          {legalLong && (
            <span className="lyrd_ft__legal t-font-alt-xs m-0" data-testid="cobrand-legal-text">
              {legalLong}
            </span>
          )}
        </div>
      </div>
    </StyledMerchandisingLayeredFeature>
  );

  return !isTargetContentLoaded ? <SkeletonLoaderStyles>{layeredFeature}</SkeletonLoaderStyles> : layeredFeature;
}
